import { Context, SpanOptions, trace, Tracer } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { otelTracesUrl } from '../constants/config';
import { isDevFeatureEnabled } from '../constants/dev';
import { getAuthToken } from '../lib/token';
import { CustomOTLPTraceExporter } from './CustomOTLPTraceExporter';
import {
  getBandwidthSummary,
  startBandwidthObserver,
} from './measureDownloadTime';
import { getLatencySummary, startLatencyCollection } from './measureLatency';

const otelTimestampToSeconds = ({
  startTime,
  endTime,
}: {
  startTime: number[];
  endTime: number[];
}) => {
  const [startTimeSeconds, startTimeNanoSeconds] = startTime;
  const [endTimeSeconds, endTimeNanoSeconds] = endTime;

  if (
    !startTimeSeconds ||
    !endTimeSeconds ||
    !startTimeNanoSeconds ||
    !endTimeNanoSeconds
  ) {
    return 'missing';
  }

  return (
    endTimeSeconds -
    startTimeSeconds +
    (endTimeNanoSeconds - startTimeNanoSeconds) / 1_000_000_000
  );
};

export const initializeOpenTelemetry = (url: string) => {
  // Create resource with service information
  const resource = resourceFromAttributes({
    'service.name': 'elis-frontend',
  });

  // Create exporter with proper configuration
  const exporter = new CustomOTLPTraceExporter(
    {
      url,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    getAuthToken
  );

  // Add custom export handling for debugging
  const originalExport = exporter.export.bind(exporter);
  exporter.export = (spans, resultCallback) => {
    if (isDevFeatureEnabled('console-log')) {
      spans.forEach((span, index) => {
        // oxlint-disable-next-line no-console
        console.log(`📊 Exporting Span ${index + 1}:`, {
          duration: otelTimestampToSeconds({
            startTime: span.startTime,
            endTime: span.endTime,
          }),
          name: span.name,
          traceId: span.spanContext().traceId,
          spanId: span.spanContext().spanId,
          status: span.status,
        });
      });
    }

    return originalExport(spans, result => {
      resultCallback(result);
    });
  };

  // Create tracer provider with span processor
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [new SimpleSpanProcessor(exporter)],
  });

  // Register the provider globally. Before registration tracer won't do anything
  provider.register();

  // Start bandwidth observer once
  startBandwidthObserver();

  // Start latency collection from all axios responses
  startLatencyCollection();

  if (isDevFeatureEnabled('console-log')) {
    // oxlint-disable-next-line no-console
    console.log('OpenTelemetry initialized successfully');
  }
};

// we wrap the native tracer here to avoid having to manually initialize open telemetry in another place.
// This allows us to control the intialization in one place at run-time.
export const createTracer = (): Tracer => {
  const tracer = trace.getTracer('elis-frontend');

  if (!otelTracesUrl) {
    return tracer;
  }
  const url = otelTracesUrl;
  let initialized = false;

  const initialize = () => {
    initializeOpenTelemetry(url);
    initialized = true;
  };

  return {
    startSpan: (name: string, options: SpanOptions, context: Context) => {
      if (!initialized) {
        initialize();
      }
      const span = tracer.startSpan(name, options, context);
      const originalEnd = span.end.bind(span);

      // Override only the end method, preserve all other methods
      span.end = () => {
        const { bytesPerSecondMean, ttfbMeanMs } = getBandwidthSummary();

        if (bytesPerSecondMean) {
          span.setAttribute('r8.sli.v1.client.speed', bytesPerSecondMean);
        }

        if (ttfbMeanMs) {
          span.setAttribute('r8.sli.v1.client.ttfb_ms', ttfbMeanMs);
        }

        const { networkLatencyMeanMs } = getLatencySummary();

        if (networkLatencyMeanMs) {
          span.setAttribute(
            'r8.sli.v1.client.network_latency_ms',
            networkLatencyMeanMs
          );
        }

        originalEnd();
      };

      return span;
    },
    // currently not used.
    startActiveSpan: () => {},
  };
};

export const tracer = createTracer();
